import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { mediaErrorFromBody, mediaErrorFromInvoke } from "./mediaInvokeError";
import { LEAGUE_ACCESS_REVIEW_INVALIDATES } from "./mediaInvalidation";
import { isDeleted } from "./mediaDeletedLogic";
import {
    LeagueAccessFilters,
    OrgLeagueAccess,
    ReviewLeagueAccessPayload,
    ReviewLeagueAccessResponse,
} from "../types/media";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";

// ============================================================================
// League access (org_league_access) — the org-only model's per-league grant.
// The organization requests access from the mobile app (request-league-access);
// the league grants / rejects / revokes it here through the mobile-owned
// `review-league-access` edge function, which also pushes the org owner. The
// in-app notification row is written by the org_league_access DB trigger.
// Reads go direct-client (org_league_access_admin_all RLS).
// ============================================================================

const LEAGUE_ACCESS_SELECT = `
    *,
    organization:media_organizations!organization_id (
      id,
      name,
      name_ar,
      org_type,
      logo_url,
      account_kind,
      team_id,
      is_approved,
      deleted_at,
      team:teams!team_id (id, name, name_ar)
    ),
    league:leagues!league_id (
      name,
      name_ar
    )
  `;

/** Resolve granted_by → reviewer name (auth.users FK: no PostgREST embed). */
async function attachGrantedByNames(rows: OrgLeagueAccess[]): Promise<void> {
    const ids = [
        ...new Set(rows.map((r) => r.granted_by).filter((id): id is string => !!id)),
    ];
    if (ids.length === 0) return;
    // Best-effort: a failure leaves the name null («—» in the UI).
    const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", ids);
    const nameById = new Map((profiles ?? []).map((p: any) => [p.id, p.full_name]));
    for (const row of rows) {
        row.granted_by_name = row.granted_by ? nameById.get(row.granted_by) ?? null : null;
    }
}

interface UseLeagueAccessRequestsOptions {
    filters?: LeagueAccessFilters;
    enabled?: boolean;
}

/**
 * Access requests / grants of the currently selected league ("Accès ligues"
 * tab and the bell). Not season-scoped: an access persists across seasons.
 */
export function useLeagueAccessRequests(options: UseLeagueAccessRequestsOptions = {}) {
    const { filters = {}, enabled = true } = options;
    const currentLeagueId = useLeagueSeasonStore((s) => s.currentLeagueId);

    return useQuery<OrgLeagueAccess[]>({
        queryKey: ["league-access-requests", currentLeagueId, filters],
        queryFn: async () => {
            let query = supabase
                .from("org_league_access")
                .select(LEAGUE_ACCESS_SELECT)
                .order("updated_at", { ascending: false });
            if (currentLeagueId) query = query.eq("league_id", currentLeagueId);
            if (filters.status) query = query.eq("agrement_status", filters.status);

            const { data, error } = await query;
            if (error) throw new Error(error.message);
            // A deleted organisme's accesses are all revoked by the deletion;
            // the queue no longer lists them (an access is not match history).
            const rows = ((data || []) as unknown as OrgLeagueAccess[]).filter(
                (row) => !isDeleted(row.organization),
            );
            await attachGrantedByNames(rows);
            return rows;
        },
        enabled: enabled && !!currentLeagueId,
        staleTime: 30000,
    });
}

/** Every league access of one organization (organization drawer). */
export function useOrgLeagueAccess(orgId: string | null) {
    return useQuery<OrgLeagueAccess[]>({
        queryKey: ["org-league-access", orgId],
        queryFn: async () => {
            if (!orgId) return [];
            const { data, error } = await supabase
                .from("org_league_access")
                .select("*, league:leagues!league_id (name, name_ar)")
                .eq("organization_id", orgId)
                .order("created_at", { ascending: false });
            if (error) throw new Error(error.message);
            return (data || []) as unknown as OrgLeagueAccess[];
        },
        enabled: !!orgId,
        staleTime: 30000,
    });
}

/**
 * Grant / reject / revoke one access via `review-league-access`. Throws a
 * MediaFunctionError (code + server message) — show it with
 * localizeMediaError(t, err).
 */
export function useReviewLeagueAccess() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            payload: ReviewLeagueAccessPayload,
        ): Promise<ReviewLeagueAccessResponse> => {
            const reason = payload.reason?.trim();
            const { data, error } = await supabase.functions.invoke(
                "review-league-access",
                {
                    body: {
                        access_id: payload.access_id,
                        action: payload.action,
                        ...(reason ? { reason } : {}),
                    },
                },
            );
            if (error) throw await mediaErrorFromInvoke(error);
            const bodyError = mediaErrorFromBody(data);
            if (bodyError) throw bodyError;
            return data as ReviewLeagueAccessResponse;
        },
        onSettled: () => {
            // Settled, not success: a 409 ACCESS_NOT_REVIEWABLE means another
            // reviewer got there first, and the list must show their decision.
            LEAGUE_ACCESS_REVIEW_INVALIDATES.forEach((k) =>
                queryClient.invalidateQueries({ queryKey: [k] }),
            );
        },
    });
}
